/**
 * RCC Surface #1 — OFAC Hit triage.
 *
 * Review OFAC screening hits returned by jxrl.ofacSrch. FSR marks false-positive
 * (with rationale) or escalates to a CIP exception (writes SessionEvent +
 * queues task in RCC's CIP queue).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Pill } from '@/components/primitives';
import { formatLocal } from '@/utils/time';
import { SurfaceHeader } from '../components/SurfaceHeader';
import { ComplianceQueueRow } from '../components/ComplianceQueueRow';

interface OfacHitRow {
  hitId: string;
  customerName: string;
  sdnName: string;
  score: number;
  sdnType: string;
  cifId?: string;
  capturedAt: string;
  rationale?: string;
  status: 'open' | 'cleared' | 'escalated';
}

export function OfacTriage(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const [hits, setHits] = useState<OfacHitRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedHit, setSelectedHit] = useState<OfacHitRow | null>(null);
  const [rationale, setRationale] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      // Real implementation would call a v3 endpoint that persists OFAC hits.
      // For now the triage view shows an empty queue with a friendly note;
      // live-screening UIs call apiClient.jxrl.ofacSrch directly from RAO/RCM.
      // We keep the scaffold so the workflow is reviewable by Compliance.
      await Promise.resolve();
      if (controller.signal.aborted) return;
      setHits([]);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load OFAC hits');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  const clearHit = useCallback(
    async (hit: OfacHitRow): Promise<void> => {
      const note = rationale.trim();
      if (!note) {
        setError('Rationale is required to clear an OFAC hit.');
        return;
      }
      // Fire-and-forget SessionEvent per Rule #8.
      void apiClient.retail.events
        .write({
          eventType: 'ofac-hit-reviewed',
          eventCategory: 'compliance',
          severity: 'info',
          actor: user?.upn ?? 'unknown',
          payloadJson: JSON.stringify({ hitId: hit.hitId, disposition: 'cleared', rationale: note }),
        })
        .catch(() => undefined);
      setSelectedHit(null);
      setRationale('');
      await refresh();
    },
    [apiClient, rationale, refresh, user],
  );

  const escalate = useCallback(
    async (hit: OfacHitRow): Promise<void> => {
      // TODO: wire to RCC CIP exception creation once dedicated v3 route lands.
      void apiClient.retail.events
        .write({
          eventType: 'ofac-hit-escalated',
          eventCategory: 'compliance',
          severity: 'warn',
          actor: user?.upn ?? 'unknown',
          payloadJson: JSON.stringify({ hitId: hit.hitId, targetQueue: 'cip-exceptions' }),
        })
        .catch(() => undefined);
      setSelectedHit(null);
      await refresh();
    },
    [apiClient, refresh, user],
  );

  return (
    <div>
      <SurfaceHeader
        title="OFAC Hit Triage"
        scope="Review live OFAC screening hits. Clear false positives with rationale; escalate true matches to CIP exception queue."
        surfaceNumber={1}
        role="FSR"
        actions={
          <Button variant="ghost" size="sm" onClick={() => void refresh()}>
            Refresh
          </Button>
        }
      />

      {loading && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Loading OFAC queue…</div>}
      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-6)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {!loading && hits.length === 0 && (
        <div
          style={{
            padding: 'var(--sp-10)',
            textAlign: 'center',
            background: 'var(--ink-50)',
            border: '1px dashed var(--ink-300)',
            borderRadius: 'var(--r-md)',
            color: 'var(--ink-600)',
          }}
        >
          <strong>No open OFAC hits.</strong>
          <div style={{ marginTop: 'var(--sp-3)', fontSize: 12 }}>
            Hits are captured from live screens run in RAO / RCM. When an FSR flags a potential match it
            appears here for review.
          </div>
        </div>
      )}

      {hits.map((h) => (
        <ComplianceQueueRow
          key={h.hitId}
          title={`${h.customerName} → ${h.sdnName}`}
          subtitle={`SDN type: ${h.sdnType} · score ${(h.score * 100).toFixed(0)}%`}
          meta={`Captured ${formatLocal(h.capturedAt)}`}
          severity={h.score > 0.85 ? 'err' : h.score > 0.6 ? 'warn' : 'info'}
          slaBadge={{ variant: h.status === 'open' ? 'warn' : 'muted', label: h.status }}
          onClick={() => setSelectedHit(h)}
        />
      ))}

      {selectedHit && (
        <div
          style={{
            marginTop: 'var(--sp-8)',
            padding: 'var(--sp-8)',
            background: 'var(--surface-card)',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--sh-2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', marginBottom: 'var(--sp-5)' }}>
            <h3 style={{ margin: 0 }}>Hit detail</h3>
            <Pill variant={selectedHit.score > 0.85 ? 'err' : 'warn'}>
              Score {(selectedHit.score * 100).toFixed(0)}%
            </Pill>
          </div>
          <label
            htmlFor="ofac-rationale"
            style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 'var(--sp-3)' }}
          >
            Disposition rationale (required to clear)
          </label>
          <textarea
            id="ofac-rationale"
            rows={4}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            style={{
              width: '100%',
              padding: 'var(--sp-4)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
            }}
          />
          <div style={{ display: 'flex', gap: 'var(--sp-4)', marginTop: 'var(--sp-5)' }}>
            <Button variant="ghost" onClick={() => setSelectedHit(null)}>
              Cancel
            </Button>
            <Button variant="green" onClick={() => void clearHit(selectedHit)}>
              Clear false positive
            </Button>
            <Button variant="danger" onClick={() => void escalate(selectedHit)}>
              Escalate to CIP exception
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
